import { Close } from "@mui/icons-material";
import { Box, Button, Chip } from "@mui/material";
import React from "react";

// One active filter: what a chip shows and what its removal names
export interface ActiveFilter {
  // Names the filter to its owner; unique among the active ones
  id: string;
  // The section it belongs to, e.g. "Category"
  section: string;
  // What is picked, as words: a value's label, or a range like "2 to 10 kg"
  value: string;
}

export interface ActiveFilterChipsProps {
  filters: ActiveFilter[];
  // Not needed read-only
  onRemove?: (id: string) => void;
  onClearAll?: () => void;
  // Whether the chips only state the filters: no cross, no "Clear all". The
  // filters are changed elsewhere (the analytics page's scope bar).
  readOnly?: boolean;
}

// What a chip reads: "Section · value"
export const activeFilterText = (filter: ActiveFilter): string =>
  `${filter.section} · ${filter.value}`;

// The active filters as chips, each with a cross that removes it, followed
// by "Clear all". Renders nothing without an active filter.
const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  filters,
  onRemove,
  onClearAll,
  readOnly = false,
}) => {
  if (filters.length === 0) {
    return null;
  }

  if (readOnly) {
    return (
      <Box
        aria-label="Active filters"
        role="group"
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}
      >
        {filters.map((filter) => (
          <Chip
            key={filter.id}
            label={activeFilterText(filter)}
            size="small"
            sx={{ maxWidth: "100%" }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      aria-label="Active filters"
      role="group"
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}
    >
      {filters.map((filter) => {
        const text = activeFilterText(filter);
        return (
          <Chip
            key={filter.id}
            label={text}
            size="small"
            onDelete={() => onRemove?.(filter.id)}
            // The cross is named, so that it is not hidden from assistive
            // technology as a bare icon is. By keyboard the chip itself
            // removes on Backspace or Delete.
            deleteIcon={
              <Close
                role="button"
                aria-hidden={false}
                aria-label={`Remove ${text}`}
              />
            }
            sx={{ maxWidth: "100%" }}
          />
        );
      })}
      <Button
        variant="text"
        size="small"
        onClick={() => onClearAll?.()}
        sx={{ textTransform: "none", minWidth: 0, py: 0 }}
      >
        Clear all
      </Button>
    </Box>
  );
};

export default ActiveFilterChips;
